/**
 * The quick-capture composer: a textarea that takes pasted or dropped screenshots, a space
 * choice, and Ctrl+Enter to save. Shared between the stream page and the global quick-capture
 * dialog (hotkey C) — thought to saved item in under five seconds is the contract (PLAN.md
 * förmåga 1 and 26).
 */
import type { ClipboardEvent, DragEvent, KeyboardEvent } from "react";
import { useCallback, useRef, useState } from "react";

import {
  ApiError,
  api,
  uploadAttachment,
  type AttachmentInfo,
  type CaptureInfo,
  type Space,
} from "../../api";
import { Button } from "../../components/primitives";
import { useToast } from "../../components/toast";

const LAST_SPACE_KEY = "noteshelf.capture-space";

/** Announces a new capture app-wide, so an open stream view can show it immediately. */
export const CAPTURE_CREATED_EVENT = "noteshelf:capture-created";

export function CaptureComposer({
  spaces,
  autoFocus,
  cta = false,
  onCreated,
}: {
  spaces: Space[];
  autoFocus?: boolean;
  /** The stream page's one Glöden button lives here; in the dialog it is a primary. */
  cta?: boolean;
  onCreated?: (capture: CaptureInfo) => void;
}) {
  const showToast = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const remembered = localStorage.getItem(LAST_SPACE_KEY);
  const [spaceId, setSpaceId] = useState<string>(
    spaces.some((space) => space.id === remembered) ? remembered! : (spaces[0]?.id ?? ""),
  );
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);
  const [uploading, setUploading] = useState(0);
  const [saving, setSaving] = useState(false);

  const addFiles = useCallback(
    async (files: File[]) => {
      if (!spaceId || files.length === 0) {
        return;
      }
      setUploading((count) => count + files.length);
      for (const file of files) {
        try {
          const uploaded = await uploadAttachment(spaceId, file);
          setAttachments((current) =>
            current.some((a) => a.id === uploaded.id) ? current : [...current, uploaded],
          );
        } catch (error) {
          showToast({
            message:
              error instanceof ApiError
                ? `Uppladdningen misslyckades: ${error.message}`
                : "Uppladdningen misslyckades.",
            tone: "fail",
          });
        } finally {
          setUploading((count) => count - 1);
        }
      }
    },
    [spaceId, showToast],
  );

  const onPaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const files = [...event.clipboardData.files];
      if (files.length > 0) {
        event.preventDefault();
        void addFiles(files);
      }
    },
    [addFiles],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLTextAreaElement>) => {
      const files = [...event.dataTransfer.files];
      if (files.length > 0) {
        event.preventDefault();
        void addFiles(files);
      }
    },
    [addFiles],
  );

  const canSave = !saving && uploading === 0 && (body.trim() !== "" || attachments.length > 0);

  const save = useCallback(async () => {
    if (!canSave || !spaceId) {
      return;
    }
    setSaving(true);
    try {
      const capture = await api.createCapture({
        space_id: spaceId,
        body: body.trim(),
        attachment_ids: attachments.map((a) => a.id),
      });
      localStorage.setItem(LAST_SPACE_KEY, spaceId);
      setBody("");
      setAttachments([]);
      textareaRef.current?.focus();
      window.dispatchEvent(new CustomEvent(CAPTURE_CREATED_EVENT, { detail: capture }));
      onCreated?.(capture);
    } catch (error) {
      showToast({
        message: error instanceof ApiError ? error.message : "Fångsten kunde inte sparas.",
        tone: "fail",
      });
    } finally {
      setSaving(false);
    }
  }, [canSave, spaceId, body, attachments, onCreated, showToast]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
        event.preventDefault();
        void save();
      }
    },
    [save],
  );

  return (
    <div className="grid gap-s4">
      <label className="grid gap-s2">
        <span className="nb-overline border-0 pb-0">Fånga en tanke</span>
        <textarea
          ref={textareaRef}
          autoFocus={autoFocus}
          rows={3}
          className="nb-field min-h-[72px] resize-y py-s3"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          onPaste={onPaste}
          onDrop={onDrop}
          onKeyDown={onKeyDown}
        />
        <span className="text-small text-muted">
          Klistra in en skärmdump direkt i fältet. Ctrl + Enter sparar.
        </span>
      </label>

      {(attachments.length > 0 || uploading > 0) && (
        <ul className="flex flex-wrap items-center gap-s4">
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className="flex items-center gap-s3 rounded-control border border-line bg-surface-2 px-s3 py-s2"
            >
              {attachment.mime_type.startsWith("image/") ? (
                <img
                  src={attachment.thumbnail_url ?? attachment.url}
                  alt={attachment.filename}
                  className="h-s7 w-s7 rounded-control object-cover"
                />
              ) : (
                <span className="text-small text-muted">📄</span>
              )}
              <span className="max-w-[16ch] truncate text-small text-ink">
                {attachment.filename}
              </span>
              <button
                type="button"
                aria-label={`Ta bort ${attachment.filename}`}
                className="text-small text-muted hover:text-ink"
                onClick={() =>
                  setAttachments((current) => current.filter((a) => a.id !== attachment.id))
                }
              >
                ✕
              </button>
            </li>
          ))}
          {uploading > 0 && <li className="text-small text-muted">Laddar upp …</li>}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-s4">
        <label className="grid gap-s2">
          <span className="nb-overline border-0 pb-0">Utrymme</span>
          <select
            className="nb-field"
            value={spaceId}
            onChange={(event) => setSpaceId(event.target.value)}
          >
            {spaces.map((space) => (
              <option key={space.id} value={space.id}>
                {space.kind === "private" ? `${space.name} (privat)` : space.name}
              </option>
            ))}
          </select>
        </label>
        <Button
          variant={cta ? "cta" : "primary"}
          className="ml-auto"
          disabled={!canSave}
          onClick={() => void save()}
        >
          {saving ? "Sparar …" : "Fånga"}
        </Button>
      </div>
    </div>
  );
}
